import {
    type ComponentType,
    createContext,
    useContext,
    useEffect,
    useState,
} from "react";

// Loaders only show once loading has taken this long, so a fast load shows no
// spinner flashing by
const DEFAULT_DELAY_MS = 300;

const LoadingDelayContext = createContext(DEFAULT_DELAY_MS);

// For pages that want their loaders to show right away
export const ImmediateLoading = ({
    children,
}: {
    children: React.ReactNode;
}) => (
    <LoadingDelayContext.Provider value={0}>
        {children}
    </LoadingDelayContext.Provider>
);

export const withImmediateLoading = <P extends object>(
    Component: ComponentType<P>,
) => {
    const WithImmediateLoading = (props: P) => (
        <ImmediateLoading>
            <Component {...props} />
        </ImmediateLoading>
    );
    return WithImmediateLoading;
};

// A loader taking over from one that was showing (e.g. the route's, then the
// page's) shows right away, so the spinner doesn't blink out in between
const HANDOVER_MS = 100;
let visibleLoaders = 0;
let lastLoaderHiddenAt = 0;

const isHandover = () =>
    visibleLoaders > 0 || Date.now() - lastLoaderHiddenAt < HANDOVER_MS;

/** Whether a loader has been up long enough to show */
export const useLoaderVisible = () => {
    const delay = useContext(LoadingDelayContext);
    const [visible, setVisible] = useState(() => delay <= 0 || isHandover());
    useEffect(() => {
        if (visible) {
            return;
        }
        const timeout = setTimeout(() => setVisible(true), delay);
        return () => clearTimeout(timeout);
    }, [visible, delay]);
    useEffect(() => {
        if (!visible) {
            return;
        }
        visibleLoaders++;
        return () => {
            visibleLoaders--;
            lastLoaderHiddenAt = Date.now();
        };
    }, [visible]);
    return visible;
};
